import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GraduationCap, Loader2, ChevronRight, ChevronDown, Clapperboard, Users, BookOpen, MessageSquareText, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api, formatDate, formatPrice, scoreBg } from '../lib/api';
import { courseCover } from '../lib/covers';
import { UserAvatar } from '../components/UserAvatar';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';

const courseFallbackGradient = (title = '') => {
  const hues = [[24, 32], [210, 226], [265, 280], [150, 165], [330, 345], [190, 205]];
  const idx = (title.charCodeAt(0) || 65) % hues.length;
  const [h1, h2] = hues[idx];
  return `linear-gradient(160deg, hsl(${h1}, 40%, 26%) 0%, hsl(${h2}, 45%, 14%) 100%)`;
};

export const CourseCard = ({ course, onClick, delay = 0 }) => {
  const { t } = useLanguage();
  const price = course.price_cents > 0 ? formatPrice(course.price_cents, course.currency) : null;
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      onClick={onClick}
      data-testid={`course-card-${course.course_id}`}
      className="ss-poster aspect-[2/3] w-full text-left group"
    >
      {/* base tone (visible only if image fails) */}
      <div className="absolute inset-0" style={{ background: courseFallbackGradient(course.title) }} />
      {/* Netflix-style real cover image */}
      <img
        src={courseCover(course)}
        alt={course.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.target.style.display = 'none'; }}
        data-testid={`course-cover-img-${course.course_id}`}
      />
      <div className="ss-poster-gradient" />

      {/* price badge */}
      <span
        data-testid={`course-price-${course.course_id}`}
        className={`absolute top-2.5 right-2.5 rounded-[12px] px-2.5 py-1 font-display font-bold text-xs ${
          price ? 'bg-white text-[#121212]' : 'bg-black/55 text-white border border-white/25 backdrop-blur-md'
        }`}
      >
        {price || t('free')}
      </span>

      {/* bottom info */}
      <div className="absolute inset-x-0 bottom-0 p-3.5 space-y-1">
        <h3 className="font-display font-bold text-white text-base leading-tight line-clamp-2">{course.title}</h3>
        <p className="text-[11px] text-white/60 truncate">{t('courseBy')} {course.coach?.name}</p>
        <div className="flex items-center gap-3 font-mono text-[10px] text-white/45">
          <span className="flex items-center gap-1"><Clapperboard size={10} /> {course.modules_count} {t('modules')}</span>
          <span className="flex items-center gap-1"><Users size={10} /> {course.enrollments_count} {t('students')}</span>
        </div>
        {course.is_enrolled && (
          <span className="inline-block font-mono text-[9px] uppercase tracking-wide text-[#9BD770] bg-[rgba(155,215,112,0.12)] border border-[rgba(155,215,112,0.3)] rounded-[8px] px-1.5 py-px">
            {t('enrolled')}
          </span>
        )}
      </div>
    </motion.button>
  );
};

const statusBadge = (status, t) => {
  if (status === 'completed') return { label: t('reviewCompleted'), cls: 'bg-[rgba(34,197,94,0.14)] text-[#22C55E] border-[rgba(34,197,94,0.3)]' };
  if (status === 'paid' || status === 'in_review') return { label: t('reviewPaid'), cls: 'bg-white/[0.1] text-white border-white/20' };
  return { label: t('reviewPendingPayment'), cls: 'bg-[rgba(233,196,106,0.12)] text-[#E9C46A] border-[rgba(233,196,106,0.3)]' };
};

export default function Academy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [tab, setTab] = useState(searchParams.get('tab') === 'reviews' ? 'reviews' : 'catalog');
  const [catalog, setCatalog] = useState(null);
  const [mine, setMine] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [expandedReview, setExpandedReview] = useState(null);

  const role = user?.role || 'player';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/academy/courses');
        setCatalog(data.items);
      } catch {
        setCatalog([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (tab === 'mine' && mine === null) {
      (async () => {
        try {
          const { data } = await api.get('/academy/my-courses');
          setMine(data.items);
        } catch {
          setMine([]);
        }
      })();
    }
    if (tab === 'reviews' && reviews === null) {
      (async () => {
        try {
          const { data } = await api.get('/academy/reviews/mine');
          setReviews(data.items);
        } catch {
          setReviews([]);
        }
      })();
    }
  }, [tab, mine, reviews]);

  const payReview = async (review) => {
    setPayingId(review.review_id);
    try {
      const { data } = await api.post('/payments/checkout', {
        product_type: 'video_review',
        product_id: review.review_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || t('paymentError'));
      setPayingId(null);
    }
  };

  const tabs = [
    { id: 'catalog', label: t('catalog') },
    { id: 'mine', label: t('myCourses') },
    { id: 'reviews', label: t('myReviews') },
  ];

  const gridOf = (items, emptyText) => (
    items === null ? (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[2/3] w-full bg-white/[0.05] rounded-[20px]" />)}
      </div>
    ) : items.length === 0 ? (
      <div className="ss-card p-10 text-center space-y-3" data-testid="academy-empty-state">
        <span className="inline-flex w-14 h-14 rounded-[16px] bg-white/[0.07] border border-white/12 items-center justify-center">
          <GraduationCap size={24} className="text-white" />
        </span>
        <p className="text-sm text-white/55">{emptyText}</p>
      </div>
    ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="academy-course-grid">
        {items.map((c, i) => (
          <CourseCard key={c.course_id} course={c} delay={Math.min(i * 0.05, 0.3)} onClick={() => navigate(`/course/${c.course_id}`)} />
        ))}
      </div>
    )
  );

  return (
    <div className="space-y-4" data-testid="academy-page">
      <div className="pt-2 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight text-white">{t('academyTitle')}</h1>
          <p className="text-sm text-white/55 mt-1">{t('academySub')}</p>
        </div>
        {(role === 'coach' || role === 'admin') ? (
          <button onClick={() => navigate('/coach')} data-testid="academy-coach-studio-cta" className="ss-btn-glass h-10 px-4 text-xs font-medium flex items-center gap-1.5 shrink-0">
            <GraduationCap size={14} /> {t('coachArea')} <ChevronRight size={13} />
          </button>
        ) : (
          <button onClick={() => navigate('/become-coach')} data-testid="academy-become-coach-link" className="ss-btn-glass h-10 px-4 text-xs font-medium flex items-center gap-1.5 shrink-0">
            <GraduationCap size={14} /> {t('becomeCoach')}
          </button>
        )}
      </div>

      <div className="flex gap-2" data-testid="academy-tabs">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            data-testid={`academy-tab-${tb.id}`}
            className={`ss-chip px-3.5 py-1.5 text-xs font-medium ${tab === tb.id ? 'ss-chip-active' : ''}`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'catalog' && gridOf(catalog, t('pitchEmptySub'))}
      {tab === 'mine' && gridOf(mine, t('emptyLibrarySub'))}

      {tab === 'reviews' && (
        reviews === null ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 w-full bg-white/[0.05] rounded-[20px]" />)}</div>
        ) : reviews.length === 0 ? (
          <div className="ss-card p-10 text-center space-y-3" data-testid="academy-reviews-empty">
            <span className="inline-flex w-14 h-14 rounded-[16px] bg-white/[0.07] border border-white/12 items-center justify-center">
              <MessageSquareText size={24} className="text-white" />
            </span>
            <p className="text-sm text-white/55">{t('noReviews')}</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="academy-reviews-list">
            {reviews.map((r) => {
              const badge = statusBadge(r.status, t);
              const expanded = expandedReview === r.review_id;
              return (
                <div key={r.review_id} className="ss-card p-4" data-testid={`review-item-${r.review_id}`}>
                  <div className="flex items-center gap-3">
                    <UserAvatar src={r.coach?.picture} alt={r.coach?.name} size={40} rounded={14} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{r.coach?.name}</div>
                      <div className="font-mono text-[10px] text-white/40 truncate">
                        {(lang === 'it' ? r.report?.move_label_it : r.report?.move_label_en) || '\u2014'} · {formatDate(r.created_at, lang)}
                      </div>
                    </div>
                    {r.report?.overall_score != null && (
                      <span className={`border rounded-[10px] px-1.5 py-0.5 font-mono text-[11px] tabular-nums ${scoreBg(r.report.overall_score)}`}>{r.report.overall_score}</span>
                    )}
                    <span className={`border rounded-[10px] px-2 py-0.5 font-mono text-[10px] ${badge.cls}`} data-testid={`review-status-${r.review_id}`}>{badge.label}</span>
                  </div>

                  {r.status === 'pending_payment' && (
                    <button
                      onClick={() => payReview(r)}
                      disabled={payingId === r.review_id}
                      data-testid={`review-pay-now-${r.review_id}`}
                      className="ss-btn-primary w-full h-11 mt-3 text-sm font-bold font-display flex items-center justify-center gap-2"
                    >
                      {payingId === r.review_id ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} {t('payNow')} · €10
                    </button>
                  )}

                  {r.status === 'completed' && r.coach_feedback && (
                    <div className="mt-3">
                      <button
                        onClick={() => setExpandedReview(expanded ? null : r.review_id)}
                        data-testid={`review-toggle-feedback-${r.review_id}`}
                        className="flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors"
                      >
                        <BookOpen size={13} /> {t('coachFeedback')} <ChevronDown size={13} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
                      </button>
                      {expanded && (
                        <p className="mt-2 text-sm text-white/80 leading-relaxed bg-white/[0.04] border border-white/[0.07] rounded-[14px] p-3.5" data-testid={`review-feedback-text-${r.review_id}`}>
                          {r.coach_feedback}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
